import React, { useEffect, useMemo, useState } from 'react';
import { CapType, updateCapType } from '../../api/captype';
import { alertApprovalIfPending, ApiError, isApprovalPending } from '../../api/request';
import { invalidateCapTypesCache } from '../../components/CapTypeBadges';
import { useDictText } from '../../components/DictSelect';
import Modal from '../../components/Modal';
import { alertDialog } from '../../utils/dialog';

// 设备侧改工艺类型绑定 (2026-09-08): 勾选 = 把本设备加进该工艺类型的 deviceKeys, 取消 = 移出.
// 数据仍是 cap_type.device_keys 单源, 这里只是换个方向编辑; 每个有变化的工艺类型发一次 update
// (走审批时每条各成一张工单, 变化条数一般个位数).

interface Props {
    open: boolean;
    deviceKey: string;
    deviceName: string;
    capTypes: CapType[];
    onClose: () => void;
}

function DeviceCapTypeBindModal({ open, deviceKey, deviceName, capTypes, onClose }: Props) {
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const lightText = useDictText('light_source');
    const actionText = useDictText('cap_action');

    const bound = useMemo(
        () => new Set(capTypes.filter((c) => (c.deviceKeys || []).includes(deviceKey)).map((c) => c.capKey)),
        [capTypes, deviceKey],
    );
    useEffect(() => {
        if (open) setSelected(new Set(bound));
    }, [open, bound]);

    const toggle = (key: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const changed = capTypes.filter((c) => selected.has(c.capKey) !== bound.has(c.capKey));

    const onSave = async () => {
        if (changed.length === 0) return;
        setBusy(true);
        try {
            const results = await Promise.all(
                changed.map((c) => {
                    const keys = (c.deviceKeys || []).filter((k) => k !== deviceKey);
                    if (selected.has(c.capKey)) keys.push(deviceKey);
                    return updateCapType(c.capKey, { deviceKeys: keys });
                }),
            );
            invalidateCapTypesCache();
            onClose();
            // 任一条转审批就提示一次 (消息文案同款)
            const pending = results.find((r) => isApprovalPending(r.message));
            if (pending) alertApprovalIfPending(pending.message);
        } catch (e) {
            alertDialog('保存失败: ' + (e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open={open} width={560} title={`绑定工艺类型 · ${deviceName}`} onClose={onClose}>
            <div className="p-5 space-y-3">
                <div className="text-xs text-slate-500">
                    勾选本设备具备的工艺类型; 与「数据字典 · 工艺类型 · 绑定」是同一份数据, 只是从设备侧编辑。
                    已选 {selected.size} / {capTypes.length}
                </div>
                {capTypes.length === 0 ? (
                    <div className="text-sm text-slate-400 py-6 text-center">暂无工艺类型, 先到数据字典新增</div>
                ) : (
                    <div className="border border-slate-100 rounded-lg divide-y divide-slate-100 max-h-[52vh] overflow-auto">
                        {capTypes.map((c) => {
                            const on = selected.has(c.capKey);
                            const desc = [c.lightSource && lightText(c.lightSource), c.action && actionText(c.action)]
                                .filter(Boolean)
                                .join(' · ');
                            return (
                                <label
                                    key={c.capKey}
                                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50"
                                >
                                    <input type="checkbox" checked={on} onChange={() => toggle(c.capKey)} />
                                    <span className={`tag ${on ? 'cap-on' : 'cap-off'}`}>{c.label}</span>
                                    <span className="text-xs text-slate-400 font-mono">{c.capKey}</span>
                                    <span className="text-xs text-slate-500 ml-auto">
                                        {desc || '非激光'} · 已绑 {(c.deviceKeys || []).length} 台
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-1">
                    <button type="button" className="btn-ghost" disabled={busy} onClick={onClose}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        disabled={busy || changed.length === 0}
                        onClick={onSave}
                    >
                        {busy ? '保存中…' : changed.length > 0 ? `保存 (${changed.length} 项变更)` : '保存'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default DeviceCapTypeBindModal;
